import React from "react";
import { Link } from "react-router-dom";

const TermsOfService: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Terms of Service
          </h1>
          <p className="text-lg text-gray-600">
            Terms and conditions for using our educational consulting services
          </p>
          <p className="text-sm text-gray-500 mt-2">
            Last updated: September 2025
          </p>
        </div>

        {/* Content */}
        <div className="bg-white rounded-lg shadow-sm p-8 space-y-8">
          {/* Introduction */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              1. Agreement to Terms
            </h2>
            <p className="text-gray-700 leading-relaxed">
              By accessing and using Raah Global's website and services, you
              agree to be bound by these Terms of Service and all applicable
              laws and regulations. If you do not agree with any of these terms,
              you are prohibited from using our services.
            </p>
          </section>

          {/* About Our Services */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              2. Our Services
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Raah Global provides educational consulting services specializing
              in international university admissions, particularly for South
              Korean institutions. Our services include:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2">
              <li>University and program selection guidance</li>
              <li>Application preparation and submission assistance</li>
              <li>Scholarship and financial aid guidance</li>
              <li>Document verification and translation support</li>
              <li>Visa application assistance</li>
              <li>Pre-departure and orientation services</li>
              <li>Ongoing student support services</li>
            </ul>
          </section>

          {/* Service Limitations */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              3. Service Limitations and Disclaimers
            </h2>
            <div className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 p-4 rounded-lg">
                <h3 className="font-semibold text-amber-800 mb-2">
                  Important Notice:
                </h3>
                <p className="text-amber-700 text-sm">
                  Raah Global is a consulting service that assists with
                  applications. We cannot guarantee admission to any university
                  or approval of visa applications.
                </p>
              </div>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li>
                  Admission decisions are made solely by the universities and
                  are beyond our control
                </li>
                <li>Visa approvals are determined by government authorities</li>
                <li>
                  Scholarship awards are subject to university and institutional
                  policies
                </li>
                <li>
                  Program availability and requirements may change without
                  notice
                </li>
                <li>
                  We provide guidance based on available information at the time
                  of consultation
                </li>
              </ul>
            </div>
          </section>

          {/* Client Responsibilities */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              4. Client Responsibilities
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              As our client, you agree to:
            </p>
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Document Authenticity:
                </h3>
                <ul className="list-disc list-inside text-gray-700 space-y-1">
                  <li>
                    Provide only genuine, original, and unaltered documents
                  </li>
                  <li>
                    Ensure all academic records and certificates are authentic
                  </li>
                  <li>Submit accurate personal and financial information</li>
                  <li>Immediately report any changes to your circumstances</li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Communication and Cooperation:
                </h3>
                <ul className="list-disc list-inside text-gray-700 space-y-1">
                  <li>Respond promptly to our requests for information</li>
                  <li>Attend scheduled meetings and consultations</li>
                  <li>Follow application deadlines and requirements</li>
                  <li>Maintain professional communication at all times</li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Financial Obligations:
                </h3>
                <ul className="list-disc list-inside text-gray-700 space-y-1">
                  <li>Pay all agreed service fees on time</li>
                  <li>
                    Cover all third-party costs (application fees, tests, etc.)
                  </li>
                  <li>Understand that some costs are non-refundable</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Prohibited Activities */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              5. Prohibited Activities
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">You may not:</p>
            <ul className="list-disc list-inside text-gray-700 space-y-2">
              <li>Submit false, misleading, or fraudulent information</li>
              <li>Use our services for any illegal purposes</li>
              <li>Attempt to circumvent application processes</li>
              <li>Share your account or login credentials with others</li>
              <li>Interfere with the proper functioning of our website</li>
              <li>
                Copy or distribute our proprietary materials without permission
              </li>
              <li>Engage in any activity that could harm our reputation</li>
            </ul>
          </section>

          {/* Payment Terms */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              6. Payment Terms and Conditions
            </h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Service Fees:
                </h3>
                <ul className="list-disc list-inside text-gray-700 space-y-2">
                  <li>
                    Service fees must be paid according to the agreed schedule
                  </li>
                  <li>
                    All prices are clearly communicated before service
                    commencement
                  </li>
                  <li>Additional services may incur extra charges</li>
                  <li>
                    Third-party costs (university fees, test fees) are separate
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Payment Methods:
                </h3>
                <ul className="list-disc list-inside text-gray-700 space-y-2">
                  <li>We accept various payment methods as specified</li>
                  <li>All payments must be made in the currency specified</li>
                  <li>Payment confirmations will be provided</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Intellectual Property */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              7. Intellectual Property Rights
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              All content on our website, including text, graphics, logos,
              images, and software, is the property of Raah Global and is
              protected by copyright and other intellectual property laws.
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2">
              <li>
                You may not reproduce or distribute our content without
                permission
              </li>
              <li>
                Our consulting materials and methodologies are proprietary
              </li>
              <li>Client information and documents remain confidential</li>
              <li>We respect the intellectual property rights of others</li>
            </ul>
          </section>

          {/* Privacy and Confidentiality */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              8. Privacy and Confidentiality
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We are committed to protecting your privacy and maintaining the
              confidentiality of your personal information. Please refer to our
              Privacy Policy for detailed information about how we collect, use,
              and protect your data.
            </p>
            <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
              <p className="text-blue-800 text-sm">
                Your personal information will only be shared with universities,
                government agencies, and service providers as necessary for your
                applications and as permitted by law.
              </p>
            </div>
          </section>

          {/* Limitation of Liability */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              9. Limitation of Liability
            </h2>
            <div className="space-y-4">
              <p className="text-gray-700 leading-relaxed">
                To the maximum extent permitted by law, Raah Global shall not be
                liable for any indirect, incidental, special, consequential, or
                punitive damages, including but not limited to:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li>Loss of profits or business opportunities</li>
                <li>Costs of alternative services</li>
                <li>Loss of data or information</li>
                <li>Emotional distress or disappointment</li>
                <li>Third-party actions or decisions</li>
              </ul>
              <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
                <p className="text-red-800 text-sm">
                  <strong>Important:</strong> Our total liability for any claim
                  shall not exceed the amount of fees paid for our services
                  related to that specific claim.
                </p>
              </div>
            </div>
          </section>

          {/* Termination */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              10. Termination
            </h2>
            <div className="space-y-4">
              <p className="text-gray-700 leading-relaxed">
                Either party may terminate our service agreement under the
                following conditions:
              </p>
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Termination by Client:
                </h3>
                <ul className="list-disc list-inside text-gray-700 space-y-1">
                  <li>Written notice of at least 30 days</li>
                  <li>Payment of all outstanding fees</li>
                  <li>Completion of ongoing applications where possible</li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Termination by Raah Global:
                </h3>
                <ul className="list-disc list-inside text-gray-700 space-y-1">
                  <li>Violation of terms and conditions</li>
                  <li>Provision of false or fraudulent information</li>
                  <li>Non-payment of fees</li>
                  <li>Uncooperative or abusive behavior</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Governing Law */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              11. Governing Law and Jurisdiction
            </h2>
            <p className="text-gray-700 leading-relaxed">
              These Terms of Service shall be governed by and construed in
              accordance with the laws of South Korea. Any disputes arising from
              these terms or our services shall be subject to the exclusive
              jurisdiction of the courts in South Korea.
            </p>
          </section>

          {/* Changes to Terms */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              12. Changes to Terms
            </h2>
            <p className="text-gray-700 leading-relaxed">
              We reserve the right to modify these Terms of Service at any time.
              We will notify clients of any significant changes via email or
              through our website. Continued use of our services after such
              modifications constitutes acceptance of the updated terms.
            </p>
          </section>

          {/* Contact Information */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              13. Contact Information
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              If you have any questions about these Terms of Service, please
              contact us:
            </p>
            <div className="bg-cyan-50 border border-cyan-200 p-4 rounded-lg">
              <div className="space-y-2">
                <p className="text-gray-900">
                  <strong>Raah Global</strong>
                </p>
                <p className="text-gray-700">Email: info@raahglobalpak.kr</p>
                <p className="text-gray-700">
                  Business Registration: 815-79-00669
                </p>
                <p className="text-gray-700">
                  Company Name: 라아 글로벌 (Raah Global)
                </p>
              </div>
            </div>
          </section>
        </div>

        {/* Navigation Links */}
        <div className="mt-8 text-center space-x-4">
          <Link
            to="/privacy-policy"
            className="text-cyan-600 hover:text-cyan-700 font-medium"
          >
            Privacy Policy
          </Link>
          <span className="text-gray-400">|</span>
          <Link
            to="/refund-policy"
            className="text-cyan-600 hover:text-cyan-700 font-medium"
          >
            Refund Policy
          </Link>
          <span className="text-gray-400">|</span>
          <Link
            to="/"
            className="text-cyan-600 hover:text-cyan-700 font-medium"
          >
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
